import styled from '@emotion/styled';
import React, { useEffect, useState, ReactNode, KeyboardEvent } from 'react';
import {
	Button,
	Form,
	InputGroup,
	OverlayTrigger,
	Tooltip,
} from 'react-bootstrap';
import { BsSearch, BsX } from 'react-icons/bs';
import { MdArrowForward } from 'react-icons/md';
import Select, { components, createFilter, FilterOptionOption, ActionMeta, OnChangeValue, GroupBase } from 'react-select';

import type { SelectOption } from '../types/common';

const ValuesContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	margin-top: 1rem;
`;

const Value = styled.div`
	background: var(--bs-blue-100);
	color: var(--bs-primary);
	border-radius: 4px;
	align-items: center;
	width: auto;
	display: flex;
	margin: 2px;
	min-width: 0px;
	box-sizing: border-box;

	span {
		border-radius: 4px;
		font-weight: 500;
		font-size: 0.85rem;
		overflow: hidden;
		padding: 3px 3px 3px 9px;
		text-overflow: ellipsis;
		white-space: nowrap;
		box-sizing: border-box;
	}

	&:hover,
	&:focus {
		background: var(--bs-primary);
		color: var(--bs-blue-50);
		cursor: pointer;

		> button {
			background: var(--bs-primary);
			color: var(--bs-blue-50);
		}
	}
`;

const XButton = styled.button`
	all: unset;
	height: 32px;
	margin-left: 0.3rem;
	color: var(--bs-dark);
	transition: fill 0.15s ease-in-out;
	cursor: pointer;
	align-items: center;
	display: flex;
	padding-left: 4px;
	padding-right: 4px;
	box-sizing: border-box;
	border-top-right-radius: 4px;
	border-bottom-right-radius: 4px;

	&:hover,
	&:focus {
		background: var(--bs-danger-light) !important;
		color: var(--bs-danger) !important;

		svg {
			color: var(--bs-danger) !important;
		}
	}
`;

interface ControlProps {
	children: ReactNode;
	innerRef?: any;
	isFocused: boolean;
	[key: string]: any;
}

const Control: React.FC<any> = ({ children, innerRef, isFocused, ...props }) => {
	return (
		<components.Control {...props} innerRef={innerRef} isFocused={isFocused}>
			<div
				style={{
					marginLeft: 10,
					fontSize: 16,
					color: `var(--bs-${isFocused ? 'blue' : 'dark'})`,
				}}
			>
				<BsSearch />
			</div>
			{children}
		</components.Control>
	);
};

export interface BaySearchProps {
	handleSearchInput: (value: string) => void;
	onKeyDownFunc?: (event: KeyboardEvent<HTMLDivElement>) => void;
	onChangeFunc?: (value: OnChangeValue<SelectOption, boolean>, meta?: ActionMeta<SelectOption>) => void;
	optionsArray?: SelectOption[];
	defaultInputValue?: string;
	customOption?: (label: any, index: any) => ReactNode;
	customSingleValue?: (props: any) => ReactNode;
	filterConfig?: FilterOptionOption<SelectOption>;
	placeholder?: string;
	withButton?: boolean;
	showPrefix?: boolean;
	prefixText?: string;
	prefixIcon?: ReactNode;
	btnVariant?: string;
	btnFunc?: () => void;
	btnSize?: 'sm' | 'lg';
	helpText?: string;
	helpTextLabel?: string;
	showNoOptionLink?: boolean;
	noOptionLinkText?: string;
	noOptionItemText?: string;
	expandable?: boolean;
	maxWidth?: number;
	searchIcon?: boolean;
	disabled?: boolean;
	hideMenu?: boolean;
	withChips?: boolean;
	id?: string | number;
	name?: string;
	menuClassName?: string;
	isSearchOnly?: boolean;
	t?: (key: string) => string;
	autoFocusSearch?: boolean;
	isClearable?: boolean;
	selectedValue?: SelectOption | SelectOption[] | null;
	/** When provided, only characters matching this pattern are kept in the input; input is unrestricted when omitted. */
	validationPattern?: RegExp;
	tabSelectsValue?: boolean;
}

const BaySearch: React.FC<BaySearchProps> = ({
	btnFunc,
	btnSize,
	btnVariant,
	helpText,
	helpTextLabel,
	optionsArray = [],
	onChangeFunc,
	placeholder,
	prefixIcon,
	prefixText,
	showPrefix,
	withButton,
	showNoOptionLink,
	noOptionLinkText,
	noOptionItemText = 'listing',
	expandable,
	maxWidth,
	searchIcon = true,
	customOption,
	customSingleValue,
	filterConfig,
	id,
	name,
	disabled,
	withChips = false,
	handleSearchInput,
	defaultInputValue,
	selectedValue,
	onKeyDownFunc = () => {},
	hideMenu = false,
	menuClassName = '',
	t = (key) => key,
	isSearchOnly = true,
	isClearable = false,
	autoFocusSearch = false,
	validationPattern,
	tabSelectsValue = true,
}) => {
	const [searchValue, setSearchValue] = useState(
		defaultInputValue ? defaultInputValue : '',
	);
	const [value, setValue] = useState<SelectOption | SelectOption[] | null>(selectedValue || null);

	useEffect(() => {
		setSearchValue(defaultInputValue || '');
	}, [defaultInputValue]);

	useEffect(() => {
		setValue(selectedValue || null);
	}, [selectedValue]);

	// TODO:
	/**
	 * make 'buttonIcon' prop return an IconButton component
	 * that shows and hides the search input (expands the width to 100%)
	 */
	// React Select uses @emotion styled components
	// under the hood for style overrides
	const styles = {
		container: (base: any) => ({
			...base,
			flex: 1,
		}),
		control: (base: any, state: any) => ({
			...base,
			minHeight: 36,
			borderWidthLeft: showPrefix ? 0 : 1,
			borderTopLeftRadius: showPrefix ? 0 : 4,
			borderTopRightRadius: withButton ? 0 : 4,
			borderBottomRightRadius: withButton ? 0 : 4,
			borderBottomLeftRadius: showPrefix ? 0 : 4,
			borderColor: state.isFocused ? 'var(--bs-blue)' : 'hsl(0, 0%, 70%)',
			boxShadow: state.isFocused ? '0 0 0.25rem 0 var(--bs-blue)' : 'none',
			':hover': {
				borderColor: 'var(--bs-blue)',
			},
			width:
				state.isFocused && expandable
					? maxWidth
						? maxWidth
						: '100%'
					: expandable
						? 155
						: '100%',
			transition: ' all .3s',
		}),
		clearIndicator: (base: any) => ({
			...base,
			cursor: 'pointer',
			fontSize: 24,
			padding: 3,
			marginRight: 6,
		}),
		dropdownIndicator: (base: any) => ({
			...base,
			color: 'var(--bs-dark)',
			fontSize: 20,
		}),
		input: (base: any) => ({
			...base,
			width: 'auto',
			minWidth: 5,
		}),
		menu: (base: any) => ({
			...base,
			maxWidth: 540,
			fontSize: 13,
			fontWeight: 500,
			paddingTop: '0.5em',
			paddingBottom: '1em',
		}),
		noOptionsMessage: (base: any) => ({
			...base,
			color: 'var(--bs-danger)',
			textAlign: 'none',
		}),
		option: (base: any) => ({
			...base,
			borderBottom: 'solid 1px transparent',
			':last-child': { border: 0 },
			fontWeight: 400,
		}),
	};

	const ClearIndicator = (props: any) => {
		const {
			getStyles,
			innerProps: { ref, ...restInnerProps },
		} = props;
		return (
			<OverlayTrigger
				overlay={<Tooltip id="clear-button-tooltip">Clear Search</Tooltip>}
				placement="top"
				trigger={['hover', 'focus']}
			>
				<div
					{...restInnerProps}
					ref={ref}
					style={getStyles('clearIndicator', props)}
				>
					<BsX />
				</div>
			</OverlayTrigger>
		);
	};

	const MenuList = (props: any) => {
		const { inputValue } = props.selectProps;

		return (
			<components.MenuList {...props} className={`${menuClassName}`}>
				{helpText && (
					<div
						style={{
							borderBottom: '1px solid var(--bs-gray-500)',
							padding: '0.5rem',
						}}
					>
						{helpText}
					</div>
				)}
				{!isSearchOnly && inputValue?.length < 3 ? (
					<div className="text-500 d-flex justify-content-center align-items-center text-center">
						<p className="mt-2 px-2 mb-0">{t('searchFieldText')}</p>
					</div>
				) : (
					props.children
				)}
			</components.MenuList>
		);
	};

	const NoOptionsMessage = (props: any) => {
		return (
			<components.NoOptionsMessage {...props}>
				{!showNoOptionLink ? (
					<div className="text-center">
						<span className="text-dark">No results found</span>
						<p className="text-500 mt-3 mb-0">
							Please check your spelling or try a different search
						</p>
					</div>
				) : (
					<>
						Unable to locate {noOptionItemText}.
						<br />{' '}
						<span className="text-dark">
							Please search again
							{showNoOptionLink && (
								<>
									{' '}
									or{' '}
									<a
										className="text-primary"
										href={noOptionLinkText ? noOptionLinkText : '#_'}
									>
										Add Manually
									</a>
								</>
							)}
						</span>
					</>
				)}
			</components.NoOptionsMessage>
		);
	};

	const SingleValue = ({ children, ...props }: any) => {
		return (
			<components.SingleValue {...props}>
				{customSingleValue ? customSingleValue(props) : children}
			</components.SingleValue>
		);
	};

	const onChangeHandler = (value: OnChangeValue<SelectOption, boolean>, meta?: ActionMeta<SelectOption>) => {
		if (onChangeFunc) {
			onChangeFunc(value, meta);
		}
		// Convert readonly array to mutable array
		const mutableValue = Array.isArray(value) ? value.slice() : value;
		setValue(mutableValue as SelectOption | SelectOption[] | null);
	};

	const handleRemoveValue = (e: React.MouseEvent<HTMLButtonElement>) => {
		if (!onChangeFunc) return;
		const { name: buttonName } = e.currentTarget;
		const valueArray = Array.isArray(value) ? value : [];
		const removedValue = valueArray.find((val) => val.value === buttonName);
		if (!removedValue) return;
		onChangeHandler(
			valueArray.filter((val) => val.value !== buttonName),
			{ action: 'remove-value', removedValue } as ActionMeta<SelectOption>,
		);
	};

	const applyValidationPattern = (value: string) => {
		if (!validationPattern) return value;
		// Strip stateful flags so a single compiled pattern can be reused across characters
		const pattern = new RegExp(
			validationPattern.source,
			validationPattern.flags.replace(/[gy]/g, ''),
		);
		return value
			.split('')
			.filter((char) => pattern.test(char))
			.join('');
	};

	const onHandleInputChange = (newValue: string, { action }: { action: string }) => {
		if (action === 'input-change') {
			const filteredValue = applyValidationPattern(newValue);
			setSearchValue(filteredValue);
			handleSearchInput(filteredValue);
		}
	};

	const componentProps = {
		Control,
		ClearIndicator,
		DropdownIndicator: () => null,
		IndicatorSeparator: () => null,
		MenuList,
		NoOptionsMessage,
		SingleValue,
	};

	/**
	 * When 'controlShouldRenderValue' is disabled,
	 * it's recommended to also disable 'isClearable' and 'backspaceRemovesValue'.
	 */

	return (
		<>
			<InputGroup
				className={`bay-search border-0 ${helpTextLabel ? 'mb-2' : ''}`}
			>
				{showPrefix && (
					<InputGroup.Text
						className={`bg-primary text-white border-primary ${
							prefixText ? 'px-3' : ''
						}`}
					>
						{prefixText}
						<span className={`arrow-icon ${prefixText ? 'ms-1' : ''}`}>
							{prefixIcon ? prefixIcon : <MdArrowForward />}
						</span>
					</InputGroup.Text>
				)}
				<Select<SelectOption, boolean, GroupBase<SelectOption>>
					backspaceRemovesValue={!withChips}
					classNamePrefix="bay-search"
					components={
						hideMenu ? { ...componentProps, Menu: () => <></> } : componentProps
					}
					controlShouldRenderValue={!withChips}
					{...(filterConfig && { filterOption: createFilter(filterConfig as any) })}
					formatOptionLabel={
						customOption
							? (object, index) => {
								return object && customOption(object.label, index);
							}
							: undefined
					}
					aria-label={placeholder ? placeholder : 'Search'}
					id={String(id)}
					inputValue={value ? undefined : searchValue}
					isClearable={isClearable}
					isMulti={withChips}
					name={name}
					options={optionsArray}
					placeholder={placeholder ? placeholder : 'Search'}
					styles={styles}
					tabSelectsValue={tabSelectsValue}
					value={value}
					onChange={(value, meta) => onChangeHandler(value, meta)}
					onInputChange={onHandleInputChange}
					onKeyDown={(event) => {
						const len = (event.target as HTMLInputElement).value.length;
						switch (event.key) {
						case 'Home':
							if (event.shiftKey) (event.target as HTMLInputElement).selectionStart = 0;
							else (event.target as HTMLInputElement).setSelectionRange(0, 0);
							break;
						case 'End':
							if (event.shiftKey) (event.target as HTMLInputElement).selectionEnd = len;
							else (event.target as HTMLInputElement).setSelectionRange(len, len);
							break;
						}
						onKeyDownFunc(event);
					}}
					autoFocus={searchValue && searchValue.length > 0 ? true : autoFocusSearch}
					isDisabled={disabled}
				/>
				{withButton && (
					<Button
						className="py-0 pb-1"
						size={btnSize}
						variant={btnVariant ? btnVariant : 'secondary'}
						onClick={btnFunc}
					>
						<span className="fs-5">{searchIcon && <BsSearch />}</span>
					</Button>
				)}
			</InputGroup>
			{helpTextLabel && <Form.Text muted>{helpTextLabel}</Form.Text>}
			{withChips && (
				<ValuesContainer>
					{Array.isArray(value) && value?.map((val) => (
						<Value key={val.value}>
							<span>{val.label}</span>
							<XButton name={String(val.value)} onClick={handleRemoveValue}>
								<BsX size="1.5rem" />
							</XButton>
						</Value>
					))}
				</ValuesContainer>
			)}
		</>
	);
};

export default BaySearch;
